import React, { Fragment, useEffect, useState } from 'react';

import styled from 'styled-components';

import shortid from 'shortid';

import { faPlus, faTint } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Paper, ToggleButton, ToggleButtonGroup } from '@mui/material';

import { BottleHelper, pluralize, translate } from 'utils/helpers';

import {
  ButtonBlueLight, FlexContainer, FontIcon, ItemSelected, Text
} from 'components';

import { useModal } from 'hooks';
import { ModalState } from 'hooks/ModalProvider';
import { observer } from 'mobx-react-lite';
import { FluidManipulationEntryView } from 'models';
import { ManipulationCategory } from 'models/detail/FluidManipulationEntryView';
import { CircuitView } from 'models/detail/RefrigerationUnitView';
import { Controller, UseFormReturn } from 'react-hook-form';
import { SelectItem } from 'utils/types';
import { Question } from '../Question/Question';

const FluidsContainer = styled.section`
  padding-top: 1.6rem;

  &:not(:last-of-type) {
    margin-bottom: 3rem;
  }

  &:first-of-type {
    margin-top: 3rem;
  }
`;

const FluidSectionHeader = styled.p`
  margin: 0 1rem 1rem 1rem;
  color: var(--turquoise);
  font-size: 1.8rem;
  font-weight: 600;
`;

const ManipItemContainer = styled.div`
  margin: 0 1rem 1rem 1rem;
  background: var(--white);
`;

const GreenBackground = styled.div`
  margin-top: 2rem;
  padding: 1rem 2rem;
  text-align: right;
  background-color: #E4F6F4;
  border-bottom: 4px solid var(--turquoise);
`;

const TotalFluid = styled.span`
  color: var(--turquoise);
`;

const ManipItem = ({
                     countryCode,
                     expectedFluid,
                     handleForm,
                     manip,
                     manipulationType,
                     openModal,
                     removeFluidManipulation,
                     formValues
                   }: {
  countryCode: string,
  expectedFluid: SelectItem,
  formValues: any,
  handleForm: (form: FluidManipulationEntryView) => void,
  manip: FluidManipulationEntryView,
  manipulationType: ManipulationCategory,
  openModal: (state: ModalState) => void,
  removeFluidManipulation: (id: string, manipulationType: ManipulationCategory) => void
}) => (
  <ItemSelected
        deleteAction={() => removeFluidManipulation(manip.fluidBottle.id, manipulationType)}
        editAction={() => openModal({
          type: 'ADD_FLUID_MANIPULATION',
          onSubmit: handleForm,
          currentEntry: manip,
          expectedAmount: formValues.circuit && formValues.circuit.fluidCapacity,
          expectedFluid,
          fluidManipulationsOutput: formValues.fluidManipulationsOutput,
          fluidManipulationsInput: formValues.fluidManipulationsInput,
          manipulationType,
          countryCode
        })}
    >
    <FlexContainer alignItems="flex-start" flex="1" flexDirection="column">
      <Text fontSize="1.8rem" margin="0 0 0.5rem 0">
        {translate(`manipulations.${manip.manipulationType.value}`)}
      </Text>
      <FlexContainer justifyContent="flex-end">
        <Text color="var(--grey)" display="inline-block" margin="0">
          <FontIcon icon="icon-container" marginLeft="-0.5rem" marginRight="0.7rem" />
        </Text>
        <Text color="var(--grey)" display="inline-block" margin="0">
          {(manip.fluidBottle.bottleType && manip.fluidBottle.bottleType.label) ? (
                BottleHelper.getTranslatedType({
                  type: manip.fluidBottle.bottleType.key,
                  isNewFluid: manip.fluidBottle.isNewFluid,
                  identifier: manip.fluidBottle.identifier
                })
            ) : (
                `${translate('bottles.CREATED_BOTTLE')} ${manip.fluidBottle.identifier ? `(${manip.fluidBottle.identifier})` : ''}`
            )}
        </Text>
      </FlexContainer>
      <Text color="var(--grey)" margin="0">
        <FontAwesomeIcon icon={faTint} style={{ marginRight: '1rem' }} />
        {`${manip.fluid.label} / ${manip.fluidAmount}${translate('common.weightUnit')}`}
      </Text>
    </FlexContainer>
  </ItemSelected>
);

interface FormStateFluidsView {
  assembly?: boolean;
  circuit?: CircuitView;
  commissioning?: boolean;
  didFluidManipulationInput?: boolean;
  didFluidManipulationOutput?: boolean;
  dismantling?: boolean;
  fluidManipulationsInput?: FluidManipulationEntryView[];
  fluidManipulationsOutput?: FluidManipulationEntryView[];
  inputFluidTotal?: number;
  maintenance?: boolean;
  modification?: boolean;
  nonPeriodicControl?: boolean;
  outputFluidTotal?: number;
  periodicControl?: boolean;
  // eslint-disable-next-line camelcase
  ui_other?: boolean;
}

export const Fluids = observer(({
                                  countryCode,
                                  form
                                }: {
  countryCode: string,
  form: UseFormReturn<FormStateFluidsView>
}) => {
  const [, forceUpdate] = useState({});

  const { open, close } = useModal();

  // Destructure methods from react-hook-form
  const { control, watch, setValue } = form;

  // Watch all needed values
  const assembly = watch('assembly');
  const circuit = watch('circuit');
  const commissioning = watch('commissioning');
  const didFluidManipulationInput = watch('didFluidManipulationInput');
  const didFluidManipulationOutput = watch('didFluidManipulationOutput');
  const dismantling = watch('dismantling');
  const fluidManipulationsInput = watch('fluidManipulationsInput') || [];
  const fluidManipulationsOutput = watch('fluidManipulationsOutput') || [];
  const maintenance = watch('maintenance');
  const modification = watch('modification');
  const periodicControl = watch('periodicControl');
  const nonPeriodicControl = watch('nonPeriodicControl');
  const ui_other = watch('ui_other');
  const outputFluidTotal = watch('outputFluidTotal');
  const inputFluidTotal = watch('inputFluidTotal');
  const formValues = {
    assembly,
    circuit,
    commissioning,
    didFluidManipulationInput,
    didFluidManipulationOutput,
    dismantling,
    fluidManipulationsInput,
    fluidManipulationsOutput,
    inputFluidTotal,
    maintenance,
    modification,
    nonPeriodicControl,
    outputFluidTotal,
    periodicControl,
    ui_other
  };

  const manipulationTypeData: {
    [key in ManipulationCategory] : {
      button: keyof FormStateFluidsView,
      list: FluidManipulationEntryView[],
      listId: keyof FormStateFluidsView
    }
  } = {
    input: {
      list: fluidManipulationsInput,
      button: 'didFluidManipulationInput',
      listId: 'fluidManipulationsInput'
    },
    output: {
      list: fluidManipulationsOutput,
      button: 'didFluidManipulationOutput',
      listId: 'fluidManipulationsOutput'
    }
  };

  const canDoInput = [
    assembly,
    commissioning,
    modification,
    maintenance,
    periodicControl,
    ui_other
  ].some(type => type);

  const canDoOutput = [
    commissioning,
    modification,
    maintenance,
    periodicControl,
    dismantling,
    ui_other
  ].some(type => type);

  const handleForm = (form: FluidManipulationEntryView) => {
    const currentFluidManipulations: FluidManipulationEntryView[] = manipulationTypeData[form.type].list.map((manip: FluidManipulationEntryView) => manip);
    const existingManipIndex = currentFluidManipulations.findIndex(manip => manip.fluidBottle.id === form.fluidBottle.id);

    if (existingManipIndex > -1) {
      currentFluidManipulations[existingManipIndex] = form;
    } else {
      currentFluidManipulations.push({
        ...form
      });
    }

    setValue(manipulationTypeData[form.type].listId, currentFluidManipulations);

    forceUpdate({});
    close();
  };

  const removeFluidManipulation = (id: string, manipulationType: ManipulationCategory) => {
    const currentFluidManipulations = manipulationTypeData[manipulationType].list.filter(manip => manip.fluidBottle.id !== id);
    setValue(manipulationTypeData[manipulationType].listId, currentFluidManipulations);

    if (currentFluidManipulations.length === 0) {
      setValue(manipulationTypeData[manipulationType].button, false);
    }
    forceUpdate({});
  };

  const resetFluidManipulations = ({ manipulationType }) => {
    const messageSingle = translate('interventionForm.fluids.resetManipSingle');
    const messageMulti = translate('interventionForm.fluids.resetManipMulti');

    const fluidManipulationList = manipulationTypeData[manipulationType].list;

    open({
      type: 'WARNING',
      text: fluidManipulationList.length > 1 ? messageMulti : messageSingle,
      buttonConfirm: translate('button.accept'),
      buttonCancel: translate('button.cancel'),
      onConfirm: () => {
        setValue(manipulationTypeData[manipulationType].listId, []);
        forceUpdate({});
      },
      onClose: fluidManipulationList.length > 0 ? (() => {
        setValue(manipulationTypeData[manipulationType].button, true);
        forceUpdate({});
      }) : () => null
    });
  };

  const calculatedInputFluidTotal = Number(fluidManipulationsInput.reduce((acc, manip) => acc + Number(manip.fluidAmount), 0)).toFixed(2);
  const calculatedOutputFluidTotal = Number(fluidManipulationsOutput.reduce((acc, manip) => acc + Number(manip.fluidAmount), 0)).toFixed(2);

  const defaultFluidSelected = circuit ? {
    label: circuit.fluid && circuit.fluid.name,
    value: circuit.fluid && circuit.fluid.id
  } : null;

  useEffect(() => {
    setValue('outputFluidTotal', Number(calculatedOutputFluidTotal));
    setValue('inputFluidTotal', Number(calculatedInputFluidTotal));
  }, [calculatedOutputFluidTotal, calculatedInputFluidTotal, setValue]);

  return (
    <Fragment>
      {/*
        FLUIDS OUTPUT QUESTION
      */}
      {canDoOutput && (
        <Question label={translate('interventionForm.fluids.questionOutput')}>
          <FlexContainer alignItems="center" flexWrap="wrap" justifyContent="space-between">
            <Controller
                    control={control}
                    name="didFluidManipulationOutput"
                    render={({ field: { value, onChange } }) => (
                      <ToggleButtonGroup
                            aria-label={translate('interventionForm.fluids.questionOutput')}
                            exclusive
                            size="medium"
                            style={{ marginBottom: '1rem' }}
                            value={value}
                            onChange={(event, newValue) => {
                              if (newValue === null) return;
                              onChange(newValue);
                              forceUpdate({});
                              newValue === false && fluidManipulationsOutput.length > 0 && resetFluidManipulations({ manipulationType: 'output' });
                            }}
                        >
                        <ToggleButton
                              aria-label={translate('common.yes')}
                              data-cy="didFluidManipulationOutput"
                              value={true}
                              onClick={() => open({
                                type: 'ADD_FLUID_MANIPULATION',
                                onSubmit: handleForm,
                                onClose: () => {
                                  if (fluidManipulationsOutput.length === 0) {
                                    setValue('didFluidManipulationOutput', false);
                                    forceUpdate({});
                                  }
                                },
                                expectedAmount: circuit && circuit.fluidCapacity,
                                expectedFluid: defaultFluidSelected,
                                fluidManipulationsOutput,
                                manipulationType: 'output',
                                countryCode
                              })}
                          >
                          {translate('common.yes')}
                        </ToggleButton>
                        <ToggleButton
                              aria-label={translate('common.no')}
                              value={false}
                          >
                          {translate('common.no')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                />

            {didFluidManipulationOutput && (
            <ButtonBlueLight onClick={() => open({
                      type: 'ADD_FLUID_MANIPULATION',
                      onSubmit: handleForm,
                      onClose: () => {
                        if (fluidManipulationsOutput.length === 0) {
                          setValue('didFluidManipulationOutput', false);
                          forceUpdate({});
                        }
                      },
                      expectedAmount: circuit && circuit.fluidCapacity,
                      expectedFluid: defaultFluidSelected,
                      fluidManipulationsOutput,
                      manipulationType: 'output',
                      countryCode
                    })}
                    >
              <FontAwesomeIcon icon={faPlus} />
              {translate('interventionForm.fluids.addOutput')}
            </ButtonBlueLight>
                )}
          </FlexContainer>
        </Question>
        )}

      {/*
        FLUIDS INPUT QUESTION
      */}
      {canDoInput && (
        <Question label={translate('interventionForm.fluids.questionInput')}>
          <FlexContainer alignItems="center" flexWrap="wrap" justifyContent="space-between">
            <Controller
                    control={control}
                    name="didFluidManipulationInput"
                    render={({ field: { value, onChange } }) => (
                      <ToggleButtonGroup
                            aria-label={translate('interventionForm.fluids.questionInput')}
                            exclusive
                            size="medium"
                            style={{ marginBottom: '1rem' }}
                            value={value}
                            onChange={(event, newValue) => {
                              if (newValue === null) return;
                              onChange(newValue);
                              // Forcer un rendu immédiat
                              forceUpdate({});
                              newValue === false && fluidManipulationsInput.length > 0 && resetFluidManipulations({ manipulationType: 'input' });
                            }}
                        >
                        <ToggleButton
                              aria-label={translate('common.yes')}
                              data-cy="didFluidManipulationInput"
                              value={true}
                              onClick={() => open({
                                type: 'ADD_FLUID_MANIPULATION',
                                onSubmit: handleForm,
                                onClose: () => {
                                  if (fluidManipulationsInput.length === 0) {
                                    setValue('didFluidManipulationInput', false);
                                    forceUpdate({});
                                  }
                                },
                                expectedAmount: circuit && circuit.fluidCapacity,
                                expectedFluid: defaultFluidSelected,
                                fluidManipulationsOutput,
                                fluidManipulationsInput,
                                manipulationType: 'input',
                                countryCode
                              })}
                          >
                          {translate('common.yes')}
                        </ToggleButton>
                        <ToggleButton
                              aria-label={translate('common.no')}
                              value={false}
                          >
                          {translate('common.no')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                />

            {didFluidManipulationInput && (
            <ButtonBlueLight onClick={() => open({
                      type: 'ADD_FLUID_MANIPULATION',
                      onSubmit: handleForm,
                      onClose: () => {
                        if (fluidManipulationsInput.length === 0) {
                          setValue('didFluidManipulationInput', false);
                          forceUpdate({});
                        }
                      },
                      expectedAmount: circuit && circuit.fluidCapacity,
                      expectedFluid: defaultFluidSelected,
                      fluidManipulationsOutput,
                      fluidManipulationsInput,
                      manipulationType: 'input',
                      countryCode
                    })}
                    >
              <FontAwesomeIcon icon={faPlus} />
              {translate('interventionForm.fluids.addInput')}
            </ButtonBlueLight>
                )}
          </FlexContainer>
        </Question>
        )}

      {/*
        FLUIDS OUTPUT LIST
      */}
      <Fragment>
        {fluidManipulationsOutput && fluidManipulationsOutput.length > 0 && (
          <FluidsContainer>
            <FluidSectionHeader>
              {translate('interventionForm.fluids.allOutputDone', {
                    total: fluidManipulationsOutput.length,
                    output: pluralize('récupération', fluidManipulationsOutput.length)
                  })}
            </FluidSectionHeader>
            {fluidManipulationsOutput
                    .map((manip, index) => (
                      <ManipItemContainer data-cy={`outputAdded${index}`} key={shortid.generate()}>
                        <Paper>
                          <ManipItem
                                countryCode={countryCode}
                                expectedFluid={defaultFluidSelected}
                                formValues={formValues}
                                handleForm={handleForm}
                                manip={manip}
                                manipulationType="output"
                                openModal={open}
                                removeFluidManipulation={removeFluidManipulation}
                            />
                        </Paper>
                      </ManipItemContainer>
                    ))}

            <GreenBackground>
              <Text fontWeight={600} margin="0.5rem" textTransform="uppercase">
                {`${translate('interventionForm.fluids.outputTotal')} : `}
                <TotalFluid data-cy="fluidsTotalOutput">
                  {`${calculatedOutputFluidTotal || outputFluidTotal}${translate('common.weightUnit')}`}
                </TotalFluid>
              </Text>
            </GreenBackground>
          </FluidsContainer>
          )}
      </Fragment>

      {/*
        FLUIDS INPUT LIST
      */}
      <Fragment>
        {fluidManipulationsInput && fluidManipulationsInput.length > 0 && (
          <FluidsContainer>
            <FluidSectionHeader>
              {translate('interventionForm.fluids.allInputDone', {
                    total: fluidManipulationsInput.length,
                    input: pluralize('recharge', fluidManipulationsInput.length)
                  })}
            </FluidSectionHeader>
            {fluidManipulationsInput
                    .map((manip, index) => (
                      <ManipItemContainer data-cy={`inputAdded${index}`} key={shortid.generate()}>
                        <Paper>
                          <ManipItem
                                countryCode={countryCode}
                                expectedFluid={defaultFluidSelected}
                                formValues={formValues}
                                handleForm={handleForm}
                                manip={manip}
                                manipulationType="input"
                                openModal={open}
                                removeFluidManipulation={removeFluidManipulation}
                            />
                        </Paper>
                      </ManipItemContainer>
                    ))}

            <GreenBackground>
              <Text fontWeight={600} margin="0.5rem" textTransform="uppercase">
                {`${translate('interventionForm.fluids.inputTotal')} : `}
                <TotalFluid data-cy="fluidsTotalInput">
                  {`${calculatedInputFluidTotal || inputFluidTotal}${translate('common.weightUnit')}`}
                </TotalFluid>
              </Text>
            </GreenBackground>
          </FluidsContainer>
          )}
      </Fragment>
    </Fragment>
  );
});
